/**
 * TasksSecondaryNav — render trong slot secondary sidebar khi group.key='tasks'.
 * URL-driven: ?view=mine|created|assigned|following|stage:KEY|templates, ?project=<short>, ?list=<short>
 *
 * Project row có:
 *   - Click name → mở project board
 *   - Hover → hiện "..." button → menu (Add list, Share, Rename, Archive, Delete)
 *   - Chevron expand → show task lists nested bên dưới
 */
import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  User, Edit3, UserCheck, Eye, Activity, PauseCircle, CheckCircle2,
  FolderKanban, Search, Plus, MoreHorizontal, ChevronRight, CircleDashed,
  Share2, Pencil, Archive, Trash2, ListChecks, Star, X, Info, Settings2,
} from 'lucide-react';
import api from '@host/axios';
import toast from 'react-hot-toast';
import { sid } from '../../../utils/shortId';
import { useTaskDrawer } from '../../tasks/TaskDrawerContext';
import { useAuth } from '@host/auth';
import ShareResourceModal from '../../tasks/ShareResourceModal';
// Nạp CHẬM: màn tạo dự án kéo theo bản đồ Leaflet (ô địa chỉ công trình) — nạp tĩnh là Leaflet vào bundle CHÍNH mà trang
// nào cũng tải, chỉ để phục vụ một cú bấm «Tạo dự án mới».
const ProjectCreateModal = lazy(() => import('../../projects/ProjectCreateModal'));
import { useConfirm } from '../../common/ConfirmDialog';

import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { i18n } from '../../../i18n/runtime.js';

// HẰNG NGOÀI COMPONENT ⇒ phải `msg` + `i18n._()` ở CHỖ ĐỌC. Viết chuỗi trần ở đây thì bản
// production strip tiếng Việt khỏi component nhưng hằng thì không có msgid nào để tra ⇒ cột
// trái của màn Công việc ra tiếng Việt giữa giao diện tiếng Anh, không lỗi nào báo (đúng điểm
// mù đã ghi ở docs/I18N.md).
const FILTERS = [
  { key: 'mine',      label: msg`Của tôi`,  icon: User,      countKey: 'owned' },
  { key: 'created',   label: msg`Tôi tạo`,  icon: Edit3,     countKey: 'created' },
  { key: 'assigned',  label: msg`Tôi giao`, icon: UserCheck, countKey: 'assigned' },
  { key: 'following', label: msg`Theo dõi`, icon: Eye,       countKey: 'following' },
];
// Số dự án thanh bên lấy về. Đặt tên vì có chỗ thứ hai cần biết con số này: nếu lấy về ĐỦ
// ngần đó thì danh sách có thể đã bị cắt, và mọi kết luận kiểu "không dự án nào bật check-in"
// đều không đáng tin. Core có 127 dự án, khách Sky có 469.
const GIOI_HAN_DU_AN = 100;

const STATUS = [
  { key: 'stage:IN_PROGRESS', label: msg`Đang làm`,   icon: Activity,     countKey: 'in_progress' },
  { key: 'stage:PAUSED',      label: msg`Tạm ngưng`,  icon: PauseCircle,  countKey: 'paused' },
  { key: 'stage:COMPLETED',   label: msg`Hoàn thành`, icon: CheckCircle2, countKey: 'completed' },
];

export default function TasksSecondaryNav() {
  const {
    t
  } = useLingui();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user: currentUser } = useAuth();
  const { version, bumpVersion, projectsVersion, bumpProjectsVersion } = useTaskDrawer();
  const [confirmDialog, ConfirmUI] = useConfirm();
  const view = searchParams.get('view') || 'mine';
  const projectId = searchParams.get('project') || null;
  const listId = searchParams.get('list') || null;
  const isAdmin = currentUser?.role === 'admin';

  const [counts, setCounts] = useState({});   // overdue per nhóm → badge cảnh báo đỏ
  const [totals, setTotals] = useState({});   // tổng task per nhóm → badge số (TC-325)
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  // Lượt hỏi dự án gần nhất — lượt cũ về sau lượt mới thì bỏ, không thì cột trái nhảy ngược
  // về kết quả của mấy chữ đầu.
  const projectsSeqRef = useRef(0);
  const [showAllModal, setShowAllModal] = useState(false);
  // «Tạo dự án mới» mở CÙNG màn tạo của trang Dự án (đủ thiết lập cơ bản — sếp Phú 29/09/2026), không còn ô gõ tên trần.
  const [taoDuAn, setTaoDuAn] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tasks.sidebar.expanded') || '{}'); } catch { return {}; }
  });
  const saveExpanded = (next) => {
    setExpanded(next);
    localStorage.setItem('tasks.sidebar.expanded', JSON.stringify(next));
  };

  // CHỈ số đếm (`summary=1`) — thanh bên không vẽ dòng việc nào. Trước 28/09/2026 nó kéo trọn gói «Việc của tôi»
  // (4,2 MB với admin Sky) chỉ để lấy mấy con số, cùng lúc với trang danh sách kéo thêm một lượt nữa. Máy chủ đời cũ
  // bỏ qua tham số và trả cả gói — vẫn đọc được `counts` / `totals` như trước.
  useEffect(() => {
    api.get('/tasks/my-tasks', { params: { summary: 1 } }).then(r => {
      const d = r.data.data || {};
      setCounts(d.counts || {});
      setTotals(d.totals || {});
    }).catch(() => {});
  }, [version]);

  // Projects fetch chỉ refetch khi cấu trúc thay đổi (add/rename/delete project hoặc tasklist)
  // hoặc khi gõ ô tìm. Không fetch khi user complete task → tránh giật sidebar.
  //
  // Ô tìm phải đi qua MÁY CHỦ: danh sách này bị cắt ở GIOI_HAN_DU_AN, nên lọc tại chỗ là chỉ
  // tìm trong 100 cái đầu — khách Sky có 469 dự án thì gõ tên một công trình có thật vẫn ra
  // "Không khớp". Lọc tại chỗ vẫn giữ ở dưới để danh sách nhúc nhích ngay trong lúc chờ.
  useEffect(() => {
    const q = search.trim();
    const seq = ++projectsSeqRef.current;
    const timer = setTimeout(() => {
      api.get('/tasks/projects', { params: { limit: GIOI_HAN_DU_AN, scope: 'focus', search: q || undefined } })
        .then(r => { if (seq === projectsSeqRef.current) setProjects(r.data.data || []); })
        .catch(() => {});
    }, q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [projectsVersion, search]);

  // Auto-expand project hiện đang xem (giúp user thấy list đang mở)
  useEffect(() => {
    if (projectId && !expanded[projectId]) {
      saveExpanded({ ...expanded, [projectId]: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const setView = (v) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', v);
    next.delete('project');
    next.delete('list');
    next.delete('taskId');
    if (window.location.pathname !== '/tasks') navigate(`/tasks?${next.toString()}`);
    else setSearchParams(next, { replace: true });
  };
  const openProject = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('project', sid(id));
    next.delete('view');
    next.delete('list');
    next.delete('taskId');
    if (window.location.pathname !== '/tasks') navigate(`/tasks?${next.toString()}`);
    else setSearchParams(next, { replace: true });
  };
  // Mở dự án VÀ bật sẵn ngăn cài đặt (`?open=settings`, TasksPage đọc rồi xoá tham số).
  // Vào dự án xong bắt người ta tự đi tìm nút Tùy chỉnh là thừa một bước — nhất là khi
  // thanh công cụ hẹp đã gom nút đó vào menu "⋯".
  const openProjectSettings = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('project', sid(id));
    next.set('open', 'settings');
    next.delete('view');
    next.delete('list');
    next.delete('taskId');
    if (window.location.pathname !== '/tasks') navigate(`/tasks?${next.toString()}`);
    else setSearchParams(next, { replace: true });
  };
  const openList = (project, list) => {
    const next = new URLSearchParams(searchParams);
    next.set('project', sid(project.id));
    next.set('list', sid(list.id));
    next.delete('view');
    next.delete('taskId');
    if (window.location.pathname !== '/tasks') navigate(`/tasks?${next.toString()}`);
    else setSearchParams(next, { replace: true });
  };
  const goView = (v) => {
    const params = new URLSearchParams();
    params.set('view', v);
    navigate(`/tasks?${params.toString()}`);
  };

  // Trạng thái mục "Mẫu biểu mẫu" — tính từ danh sách dự án đã tải sẵn, không gọi thêm máy chủ.

  /**
   * "Bật ngay" — đưa thẳng vào màn cài đặt của dự án gần nhất, KHÔNG bắt người dùng tự mò.
   *
   * Người ta bấm nút này lúc đang đọc chú thích "chưa dự án nào bật chấm công tại chỗ". Nếu
   * chỉ mở dự án ra rồi để họ tự tìm nút Cài đặt → mở tab Check-in, thì phần lớn bỏ cuộc ở
   * bước hai. `open=checkin` là để `ProjectMain` mở sẵn ngăn cài đặt đúng mục đó.
   */
  const toggleExpand = (id) => {
    saveExpanded({ ...expanded, [id]: !expanded[id] });
  };

  const filteredProjects = projects.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase())
  );

  // ── Mục «Circle» ────────────────────────────────────────────────────────────────────────
  // ⚠️ Gác bằng chính DỮ LIỆU, không cờ cấu hình: hệ nào có cửa `/tasks/circles/config` thì
  // hiện, hệ chưa port (Core / CNV Connect) trả 404 ⇒ mục biến mất. Không có bước này thì nav
  // dùng chung mọc ra một mục bấm vào là trắng trang.
  // Hỏi MỘT LẦN cho mỗi lượt mở cụm — cửa này trả 200 kể cả khi circle đang TẮT (mục vẫn phải
  // hiện để người ta còn đường vào bật).
  const [coCircle, setCoCircle] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get('/tasks/circles/config')
      .then(() => { if (alive) setCoCircle(true); })
      .catch(() => { /* hệ chưa có tính năng ⇒ không vẽ mục */ });
    return () => { alive = false; };
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-3 border-b border-slate-100 shrink-0">
        <h1 className="text-xl font-bold text-slate-900"><Trans>Nhiệm vụ</Trans></h1>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
        <Section>
          {FILTERS.map(v => (
            <NavBtn key={v.key} icon={v.icon} label={i18n._(v.label)} overdue={counts[v.countKey]} total={totals[v.countKey]}
              active={!projectId && view === v.key}
              onClick={() => setView(v.key)} />
          ))}
        </Section>

        {/* Circle đứng RIÊNG một khối, không nhét chung với bốn bộ lọc bên trên: chúng lọc
            «việc của ai», còn đây mở sang một MÀN khác (nhịp làm việc kiểu sprint). Gộp vào
            là người dùng bấm tưởng đổi bộ lọc rồi ngạc nhiên vì cả trang đổi. */}
        {coCircle && (
          <Section>
            <NavBtn icon={CircleDashed} label={t`Circle`}
              active={pathname.startsWith('/tasks/circles')}
              onClick={() => navigate('/tasks/circles')} />
          </Section>
        )}

        <Section title={t`Trạng thái`}>
          {STATUS.map(v => (
            <NavBtn key={v.key} icon={v.icon} label={i18n._(v.label)} overdue={counts[v.countKey]} total={totals[v.countKey]}
              active={!projectId && view === v.key}
              onClick={() => setView(v.key)} />
          ))}
        </Section>

        <Section title={t`Dự án`}>
          <div className="px-2 mb-1.5">
            <div className="relative">
              <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t`Tìm dự án...`}
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-400 focus:outline-none bg-gray-50" />
            </div>
          </div>
          <button onClick={() => setTaoDuAn(true)}
            className="mx-2 mb-1.5 w-[calc(100%-1rem)] flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-dashed border-blue-300 rounded-lg hover:bg-blue-100 hover:border-blue-400 transition">
            <Plus size={13} /> <Trans>Tạo dự án mới</Trans>
          </button>
          {taoDuAn && (
            <Suspense fallback={null}>
            <ProjectCreateModal onClose={() => setTaoDuAn(false)}
              onCreated={(p) => { setTaoDuAn(false); bumpProjectsVersion(); if (p?.id) openProject(p.id); }} />
            </Suspense>
          )}
          {filteredProjects.length === 0 && (
            <p className="text-xs text-gray-400 px-3 py-1.5">{search ? t`Không khớp` : t`Chưa có dự án`}</p>
          )}
          {filteredProjects.map(p => (
            <ProjectRow key={p.id} project={p}
              isActive={projectId === sid(p.id) && !listId}
              activeListId={projectId === sid(p.id) ? listId : null}
              expanded={!!expanded[sid(p.id)]}
              onToggleExpand={() => toggleExpand(sid(p.id))}
              onOpenProject={() => openProject(p.id)}
              onOpenSettings={() => openProjectSettings(p.id)}
              onOpenList={(l) => openList(p, l)}
              onChanged={() => { bumpProjectsVersion(); }}
              confirmDialog={confirmDialog} />
          ))}
          {isAdmin && (
            <button onClick={() => setShowAllModal(true)}
              className="mt-1 w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50/50 rounded-lg transition">
              <FolderKanban size={11} />
              <span className="flex-1 text-left"><Trans>Tất cả dự án hệ thống...</Trans></span>
              <ChevronRight size={11} />
            </button>
          )}
        </Section>

      </div>

      {/* «Mẫu dự án» + «Mẫu biểu mẫu» ĐÃ DỜI sang Dự án ▸ Cài đặt (sếp Phú chốt 03/09/2026).
          Chúng từng ghim ở đáy khối này từ 22/08 vì lý do KHẢ NĂNG NHÌN THẤY — "cố định ở dưới
          cùng để ai cũng thấy" — chứ không phải vì thuộc về cụm Việc: mẫu dự án là khuôn tạo
          DỰ ÁN, mẫu biểu mẫu là phiếu hỏi lúc thợ bấm vào/ra ở CÔNG TRƯỜNG.

          ⛔ ĐỪNG dựng lại ở đây. Một thứ một chỗ — cùng luật với công tắc «Múi giờ đa vùng»
          (sếp Phú 03/09: "setting của dự án thì cứ để duy nhất ở dự án"). Đường cũ
          `/tasks?view=templates` và `?view=form-templates` VẪN SỐNG nên link đã gửi không chết.
          Khối thẻ dẫn: `@cnv/ui/tasks/ProjectTemplateLinks`. */}

      {showAllModal && (
        <AllProjectsModal onClose={() => setShowAllModal(false)}
          onChanged={() => { bumpProjectsVersion(); }}
          onOpenProject={(id) => { setShowAllModal(false); openProject(id); }} />
      )}
      {ConfirmUI}
    </div>
  );
}

function Section({ title, titleAction, children }) {
  return (
    <div>
      {title && (
        <div className="flex items-center justify-between px-3 mb-1.5">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{title}</p>
          {titleAction}
        </div>
      )}
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

/** Mục ghim đáy — không có badge đếm, vì đây là nơi ĐI TỚI chứ không phải nhóm việc. */

function NavBtn({ icon: Icon, label, overdue = 0, total = 0, active, onClick }) {
  const {
    t
  } = useLingui();

  // Badge = số việc CHƯA HOÀN THÀNH và ĐÃ TRỄ HẸN → đỏ, ẩn khi 0. Đây là CẢNH BÁO
  // "cần xử lý ngay", không phải thống kê nhóm.
  //
  // ⛔ ĐỪNG đổi lại thành TỔNG số việc. Đã thử 03.07.2026 (5aa24546, gói trong một
  // commit "checkpoint WIP") và hỏng hai đường cùng lúc:
  //   1. Core không trả `totals` ⇒ badge TẮT HẲN cả 7 mục suốt từ đó, kể cả nhóm
  //      đang có việc trễ — vì `undefined > 0` giống hệt `0 > 0`, không lỗi nào báo.
  //   2. Kể cả có `totals`, số ra vô nghĩa: "Của tôi 117" trong đó 110 việc ĐÃ XONG.
  // Tổng nhóm vẫn hữu ích nhưng thuộc về tooltip, không thuộc về badge.
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition ${
        active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
      }`}>
      <Icon size={14} className="shrink-0" />
      <span className="flex-1 text-left">{label}</span>
      {overdue > 0 && (
        <span className="text-xs px-1.5 py-0.5 rounded-md font-semibold bg-red-100 text-red-700"
          title={total > 0 ? t`${overdue} việc trễ hẹn / ${total} việc` : t`${overdue} việc trễ hẹn`}>
          {overdue}
        </span>
      )}
    </button>
  );
}

function ProjectRow({ project, isActive, activeListId, expanded, onToggleExpand, onOpenProject, onOpenSettings, onOpenList, onChanged, confirmDialog }) {
  const {
    t
  } = useLingui();

  const [showMenu, setShowMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(project.name);
  const [adding, setAdding] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [shareTarget, setShareTarget] = useState(null); // { type, id, name }
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!showMenu) return;
    const onDown = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)
          && menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showMenu]);

  useLayoutEffect(() => {
    if (!showMenu || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    // Mở menu BÊN PHẢI của trigger button (sidebar trái nên menu mở ra phải)
    setMenuPos({ top: r.top, left: r.right + 4 });
  }, [showMenu]);

  const lists = Array.isArray(project.task_lists) ? project.task_lists : [];

  const doRename = async () => {
    const v = newName.trim();
    setRenaming(false);
    if (!v || v === project.name) return;
    try { await api.put(`/tasks/projects/${sid(project.id)}`, { name: v }); toast.success(t`Đã đổi tên`); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.message || t`Lỗi đổi tên`); }
  };
  const doArchive = async () => {
    const ok = await confirmDialog?.({
      title: t`Lưu trữ dự án`,
      message: t`Lưu trữ dự án "${project.name}"?`,
      variant: 'warning',
      confirmText: t`Lưu trữ`,
    });
    if (!ok) return;
    try { await api.put(`/tasks/projects/${sid(project.id)}`, { status: 'archived' }); toast.success(t`Đã lưu trữ`); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.message || t`Lỗi`); }
  };
  const doDelete = async () => {
    const ok = await confirmDialog?.({
      title: t`Xóa dự án`,
      message: t`Xóa dự án "${project.name}"? Tất cả task trong dự án sẽ mất.`,
      variant: 'danger',
      confirmText: t`Xóa`,
    });
    if (!ok) return;
    try { await api.delete(`/tasks/projects/${sid(project.id)}`); toast.success(t`Đã xóa`); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.message || t`Lỗi`); }
  };
  const doAddList = async () => {
    const v = newListName.trim();
    if (!v) { setAdding(false); return; }
    try {
      await api.post(`/tasks/projects/${sid(project.id)}/lists`, { name: v });
      toast.success(t`Đã thêm list`);
      setAdding(false); setNewListName('');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.message || t`Lỗi`); }
  };
  const doShare = () => {
    setShareTarget({ type: 'project', id: project.id, name: project.name });
    setShowMenu(false);
  };
  const doShareList = (l) => setShareTarget({ type: 'list', id: l.id, name: l.name });

  const togglePin = async (e) => {
    e.stopPropagation();
    try {
      if (project.is_pinned) {
        await api.delete(`/tasks/projects/${sid(project.id)}/pin`);
        toast.success(t`Đã bỏ pin`);
      } else {
        await api.post(`/tasks/projects/${sid(project.id)}/pin`);
        toast.success(t`Đã pin`);
      }
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.message || t`Lỗi pin`); }
  };

  return (
    <div className="relative">
      {/* Project row */}
      <div className={`group flex items-center gap-1 pl-1 pr-1 py-1 rounded-xl transition ${
        isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
      }`}>
        <button onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
          className={`p-0.5 rounded hover:bg-black/10 transition shrink-0 ${isActive ? 'text-blue-700' : 'text-gray-400'}`}>
          <ChevronRight size={12} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        {renaming ? (
          <input value={newName} onChange={e => setNewName(e.target.value)} autoFocus
            onBlur={doRename}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setRenaming(false); setNewName(project.name); } }}
            className="flex-1 min-w-0 bg-white border border-blue-400 rounded px-1.5 py-0.5 text-sm text-gray-800 outline-none" />
        ) : (
          <button onClick={onOpenProject}
            className="flex-1 min-w-0 flex items-center gap-1.5 text-left text-sm py-0.5">
            <span className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: project.color || '#3B82F6' }} />
            <span className="flex-1 truncate">{project.name}</span>
          </button>
        )}
        {(project.task_counts?.open || 0) > 0 && !renaming && (
          <span className={`text-xs px-1.5 py-0.5 rounded-md font-semibold mr-1 ${isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{project.task_counts.open}</span>
        )}
        <button onClick={togglePin}
          className={`p-1 rounded hover:bg-black/10 transition shrink-0 ${
            project.is_pinned ? 'text-amber-500' : 'opacity-0 group-hover:opacity-100 text-gray-400'
          }`}
          title={project.is_pinned ? t`Bỏ pin` : t`Pin dự án`}>
          <Star size={12} fill={project.is_pinned ? 'currentColor' : 'none'} />
        </button>
        <button ref={triggerRef} onClick={(e) => { e.stopPropagation(); setShowMenu(s => !s); }}
          className={`opacity-0 group-hover:opacity-100 ${showMenu ? 'opacity-100' : ''} p-1 rounded hover:bg-black/10 transition shrink-0 ${isActive ? 'text-blue-700' : 'text-gray-500'}`}
          title="More">
          <MoreHorizontal size={13} />
        </button>
      </div>

      {/* More menu — portal vào body để escape sidebar overflow */}
      {showMenu && createPortal(
        <div ref={menuRef}
          className="fixed w-44 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-[10000]"
          style={{ top: menuPos.top, left: menuPos.left }}>
          <MenuItem icon={Plus} label={t`Thêm tasklist`} onClick={() => { setAdding(true); setShowMenu(false); }} />
          <MenuItem icon={Settings2} label={t({ message: 'Tùy chỉnh', context: 'hành động' })} onClick={() => { setShowMenu(false); onOpenSettings?.(); }} />
          <MenuItem icon={Share2} label={t`Chia sẻ`} onClick={doShare} />
          <MenuItem icon={Pencil} label={t`Đổi tên`} onClick={() => { setRenaming(true); setNewName(project.name); setShowMenu(false); }} />
          <div className="border-t border-gray-100 my-1" />
          <MenuItem icon={Archive} label={t`Lưu trữ`} onClick={() => { doArchive(); setShowMenu(false); }} />
          <MenuItem icon={Trash2} label={t`Xóa`} danger onClick={() => { doDelete(); setShowMenu(false); }} />
        </div>,
        document.body
      )}

      {/* Inline add list input */}
      {adding && (
        <div className="ml-6 mt-1 mb-1 flex items-center gap-1.5">
          <input value={newListName} onChange={e => setNewListName(e.target.value)} autoFocus
            placeholder={t`Tên tasklist...`} className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-xs"
            onKeyDown={e => { if (e.key === 'Enter') doAddList(); if (e.key === 'Escape') { setAdding(false); setNewListName(''); } }} />
          <button onClick={doAddList} className="px-2 py-1 bg-blue-600 text-white rounded text-[10px] font-semibold"><Trans>Tạo</Trans></button>
          <button onClick={() => { setAdding(false); setNewListName(''); }} className="text-gray-400 text-[10px]">×</button>
        </div>
      )}

      {/* Nested task lists */}
      {expanded && lists.map(l => (
        <ListRow
          key={l.id}
          list={l}
          projectId={project.id}
          isActive={activeListId === sid(l.id)}
          onOpen={() => onOpenList(l)}
          onShare={() => doShareList(l)}
          onChanged={onChanged}
          confirmDialog={confirmDialog}
        />
      ))}

      {shareTarget && (
        <ShareResourceModal resource={shareTarget} onClose={() => setShareTarget(null)} />
      )}
    </div>
  );
}

function TaskListRow({ list, project, isActive, onOpen, onChanged }) {
  const {
    t
  } = useLingui();

  const [confirmDialog, ConfirmUI] = useConfirm();
  const [showMenu, setShowMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(list.name);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!showMenu) return;
    const onDown = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)
          && menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showMenu]);

  useLayoutEffect(() => {
    if (!showMenu || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setMenuPos({ top: r.top, left: r.right + 4 });
  }, [showMenu]);

  const doRename = async () => {
    const v = newName.trim();
    setRenaming(false);
    if (!v || v === list.name) return;
    try {
      await api.put(`/tasks/projects/${sid(project.id)}/lists/${sid(list.id)}`, { name: v });
      toast.success(t`Đã đổi tên`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.message || t`Lỗi đổi tên`); }
  };
  const doDelete = async () => {
    const ok = await confirmDialog({ title: t`Xóa list`, message: t`Xóa list "${list.name}"? Task trong list sẽ chuyển về list mặc định.`, variant: 'danger', confirmText: t`Xoá` });
    if (!ok) return;
    try {
      await api.delete(`/tasks/projects/${sid(project.id)}/lists/${sid(list.id)}`);
      toast.success(t`Đã xóa`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.message || t`Lỗi xóa`); }
  };

  return (
    <>
      {ConfirmUI}
      <div className={`group flex items-center gap-1 pl-7 pr-1 py-1 rounded-md text-sm transition ${
        isActive ? 'bg-blue-500 text-white font-medium' : 'text-gray-600 hover:bg-gray-50'
      }`}>
        <ListChecks size={12} className={`shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
        {renaming ? (
          <input value={newName} onChange={e => setNewName(e.target.value)} autoFocus
            onBlur={doRename}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setRenaming(false); setNewName(list.name); } }}
            className="flex-1 min-w-0 bg-white border border-blue-400 rounded px-1.5 py-0.5 text-xs text-gray-800 outline-none" />
        ) : (
          <button onClick={onOpen} className="flex-1 min-w-0 text-left truncate py-0.5">{list.name}</button>
        )}
        <button ref={triggerRef} onClick={(e) => { e.stopPropagation(); setShowMenu(s => !s); }}
          className={`opacity-0 group-hover:opacity-100 ${showMenu ? 'opacity-100' : ''} p-1 rounded hover:bg-black/10 transition shrink-0 ${isActive ? 'text-white' : 'text-gray-500'}`}
          title="More">
          <MoreHorizontal size={12} />
        </button>
      </div>
      {showMenu && createPortal(
        <div ref={menuRef}
          className="fixed w-40 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-[10000]"
          style={{ top: menuPos.top, left: menuPos.left }}>
          <MenuItem icon={Pencil} label={t`Đổi tên`} onClick={() => { setRenaming(true); setNewName(list.name); setShowMenu(false); }} />
          <div className="border-t border-gray-100 my-1" />
          <MenuItem icon={Trash2} label={t`Xóa`} danger onClick={() => { doDelete(); setShowMenu(false); }} />
        </div>,
        document.body
      )}
    </>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 transition ${danger ? 'text-red-600' : 'text-gray-700'}`}>
      <Icon size={13} />
      <span className="flex-1 text-left">{label}</span>
    </button>
  );
}

/**
 * ListRow — 1 row task list trong sidebar, hỗ trợ rename inline + share + delete (chỉ khi rỗng).
 */
function ListRow({ list, projectId, isActive, onOpen, onShare, onChanged, confirmDialog }) {
  const {
    t
  } = useLingui();

  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(list.name);

  const doRename = async () => {
    const v = newName.trim();
    setRenaming(false);
    if (!v || v === list.name) return;
    try {
      await api.put(`/tasks/projects/${sid(projectId)}/lists/${sid(list.id)}`, { name: v });
      toast.success(t`Đã đổi tên`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.message || t`Lỗi đổi tên`); }
  };

  const doDelete = async () => {
    const ok = await confirmDialog?.({
      title: t`Xóa danh sách`,
      message: t`Xóa danh sách "${list.name}"? Chỉ áp dụng khi list không có task.`,
      variant: 'danger',
      confirmText: t`Xóa`,
    });
    if (!ok) return;
    try {
      await api.delete(`/tasks/projects/${sid(projectId)}/lists/${sid(list.id)}`);
      toast.success(t`Đã xóa`);
      onChanged?.();
    } catch (e) {
      // Backend trả message rõ ("còn N task...") khi list không rỗng → toast nguyên message.
      toast.error(e.response?.data?.message || t`Lỗi xóa`);
    }
  };

  return (
    <div className={`group/list flex items-center gap-1 pl-7 pr-1 py-1.5 rounded-xl text-sm transition ${
      isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
    }`}>
      <ListChecks size={12} className={`shrink-0 ml-1 ${isActive ? 'text-blue-700' : 'text-gray-400'}`} />
      {renaming ? (
        <input value={newName} onChange={e => setNewName(e.target.value)} autoFocus
          onBlur={doRename}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setRenaming(false); setNewName(list.name); } }}
          className="flex-1 min-w-0 bg-white border border-blue-400 rounded px-1.5 py-0.5 text-xs text-gray-800 outline-none" />
      ) : (
        <button onClick={onOpen} onDoubleClick={() => { setRenaming(true); setNewName(list.name); }}
          className="flex-1 min-w-0 flex items-center text-left" title={t`Click mở · Double-click đổi tên`}>
          <span className="flex-1 truncate">{list.name}</span>
        </button>
      )}
      {!renaming && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setRenaming(true); setNewName(list.name); }}
            className="opacity-0 group-hover/list:opacity-100 p-1 rounded hover:bg-black/10 transition shrink-0 text-gray-500"
            title={t`Đổi tên`}>
            <Pencil size={11} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onShare(); }}
            className="opacity-0 group-hover/list:opacity-100 p-1 rounded hover:bg-black/10 transition shrink-0 text-gray-500"
            title={t`Chia sẻ list`}>
            <Share2 size={11} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); doDelete(); }}
            className="opacity-0 group-hover/list:opacity-100 p-1 rounded hover:bg-red-50 transition shrink-0 text-gray-400 hover:text-red-600"
            title={t`Xóa list (chỉ áp dụng khi rỗng)`}>
            <Trash2 size={11} />
          </button>
        </>
      )}
    </div>
  );
}

/* AllProjectsModal — admin browse toàn bộ dự án hệ thống, pin về sidebar. */
function AllProjectsModal({ onClose, onChanged, onOpenProject }) {
  const {
    t
  } = useLingui();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState({}); // { project_id: true }
  const handleClose = () => onClose();

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get('/tasks/projects', { params: { scope: 'all', limit: 500, search: search || undefined } });
      setProjects(r.data.data || []);
    } catch (e) { toast.error(t`Lỗi tải danh sách`); }
    finally { setLoading(false); }
  };
  // Gõ tới đâu gọi tới đó = mỗi phím một truy vấn trả VỀ CẢ DANH SÁCH kèm task_lists lồng
  // (tenant Connecteam có 5.592 dự án). Chờ 250ms cho người gõ xong rồi mới hỏi.
  useEffect(() => {
    const id = setTimeout(reload, 250);
    return () => clearTimeout(id);
    /* eslint-disable-next-line */
  }, [search]);

  // Lọc lại ở FE: nay CẢ BA hệ đều lọc sẵn theo `search` ở máy chủ (Core từ đầu —
  // projectController.list; hai hệ SaaS vá 20/08/2026 sau khi sếp Phú báo gõ vào mà danh
  // sách đứng im). Giữ lớp lọc này làm lưới đỡ cho cụm nào deploy BE chậm hơn FE — trên hệ
  // đã lọc sẵn thì đây là lọc trùng, không đổi kết quả.
  const term = search.trim().toLowerCase();
  const filtered = term
    ? projects.filter(p => (p.name || '').toLowerCase().includes(term))
    : projects;

  const togglePin = async (p) => {
    setBusy(b => ({ ...b, [p.id]: true }));
    try {
      if (p.is_pinned) await api.delete(`/tasks/projects/${sid(p.id)}/pin`);
      else            await api.post(`/tasks/projects/${sid(p.id)}/pin`);
      // Update local state ngay để feedback nhanh
      setProjects(ps => ps.map(x => x.id === p.id ? { ...x, is_pinned: !p.is_pinned } : x));
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.message || t`Lỗi`); }
    finally { setBusy(b => ({ ...b, [p.id]: false })); }
  };

  // ⚠️ PHẢI portal ra body — không phải cho gọn, mà vì lớp phủ v2 có rule
  //   html[data-ui='v2'] body[data-v2-shell]
  //     div[class*='w-[240px]'][class*='border-r'] div[class*='bg-white']
  //     { background-color: transparent !important }   (cnv-ui/src/styles/ui-v2.css)
  // để làm trong suốt các mảng trắng BÊN TRONG nav phụ. Render inline thì modal nằm đúng
  // trong cây DOM đó → tấm `bg-white` bị xoá nền, nhìn xuyên thấy bảng/biểu đồ phía sau
  // (sếp Phú báo 20/08/2026). Thêm nữa <aside> bọc nav phụ là `fixed ... z-50` nên nó tạo
  // stacking context: z-[10000] của modal bị kẹp trong tầng 50, không leo lên trên được.
  // Cùng lý do với ConfirmDialog.jsx:39-44 và menu "..." của ProjectRow ở trên.
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
      onClick={handleClose}>
      <div className="bg-white rounded-2xl shadow-xl w-[640px] max-w-[95vw] max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <FolderKanban size={18} className="text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900"><Trans>Tất cả dự án hệ thống</Trans></h3>
          </div>
          <button onClick={handleClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition"><X size={16} /></button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 shrink-0">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} autoFocus
              placeholder={t`Tìm dự án...`}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none" />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
          {loading && <p className="text-center text-xs text-gray-400 py-8"><Trans>Đang tải...</Trans></p>}
          {/* Đang tìm mà rỗng thì phải nói "không khớp", chứ "Không có dự án" đọc ra là hệ thống
              trống trơn. Dùng LẠI msgid `Không khớp` đã có ở ô tìm dự án phía trên (đã dịch đủ
              4 ngôn ngữ trong common.po) — không đẻ chuỗi mới. */}
          {!loading && filtered.length === 0 && (
            <p className="text-center text-xs text-gray-400 py-8">
              {term ? <Trans>Không khớp</Trans> : <Trans>Không có dự án</Trans>}
            </p>
          )}
          {!loading && filtered.map(p => (
            <div key={p.id} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 group">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color || '#3B82F6' }} />
              <button onClick={() => onOpenProject?.(p.id)}
                className="flex-1 min-w-0 text-left text-sm text-gray-800 truncate hover:text-blue-600 transition">
                {p.name}
              </button>
              {(p.task_counts?.open || 0) > 0 && (
                <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{p.task_counts.open}</span>
              )}
              <button onClick={() => togglePin(p)} disabled={busy[p.id]}
                className={`p-1.5 rounded hover:bg-amber-50 transition ${p.is_pinned ? 'text-amber-500' : 'text-gray-400 hover:text-amber-500'}`}
                title={p.is_pinned ? t`Bỏ pin` : t`Pin về sidebar`}>
                <Star size={14} fill={p.is_pinned ? 'currentColor' : 'none'} />
              </button>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 shrink-0 bg-gray-50/40 text-xs text-gray-500">
          <Trans>Tổng cộng</Trans> {filtered.length} <Trans>dự án — bấm ⭐ để pin về sidebar trái.</Trans>
        </div>
      </div>
    </div>,
    document.body
  );
}
